import en from './en.json';

export type MessageKey = keyof typeof en;
type Dict = Record<MessageKey, string>;

/**
 * i18n for the hosted checkout.
 *
 *  - One flat `<code>.json` per language next to `en.json`, all with exactly the keys of
 *    `en.json` (enforced at build time by scripts/check-i18n.mjs, wired into vite.config.ts).
 *  - English is bundled; the others are lazy-loaded chunks (`import.meta.glob`).
 *  - `{name}` interpolation; missing keys fall back to English.
 *  - The document gets `lang` and `dir` (Arabic is RTL).
 *  - Components re-render through `useI18n()`, which subscribes to locale changes.
 */
export interface Language {
  code: string;
  /** Native name, shown in the switcher (never translated). */
  name: string;
  dir: 'ltr' | 'rtl';
}

export const LANGUAGES: readonly Language[] = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'es', name: 'Español', dir: 'ltr' },
  { code: 'fr', name: 'Français', dir: 'ltr' },
  { code: 'de', name: 'Deutsch', dir: 'ltr' },
  { code: 'pt-BR', name: 'Português (Brasil)', dir: 'ltr' },
  { code: 'it', name: 'Italiano', dir: 'ltr' },
  { code: 'nl', name: 'Nederlands', dir: 'ltr' },
  { code: 'ja', name: '日本語', dir: 'ltr' },
  { code: 'zh-CN', name: '简体中文', dir: 'ltr' },
  { code: 'ko', name: '한국어', dir: 'ltr' },
  { code: 'tr', name: 'Türkçe', dir: 'ltr' },
  { code: 'ar', name: 'العربية', dir: 'rtl' },
  { code: 'ru', name: 'Русский', dir: 'ltr' },
];

const STORAGE_KEY = 'cv-lang';

const loaders = import.meta.glob<{ default: Dict }>(['./*.json', '!./en.json']);
const dicts: Record<string, Dict> = { en };

let current = 'en';
let version = 0;
const listeners = new Set<() => void>();

function notify(): void {
  version++;
  for (const l of listeners) l();
}

/** Map any BCP-47 tag to a supported language code, or null. */
export function resolveLanguage(tag: string | null | undefined): string | null {
  if (!tag) return null;
  const lower = tag.trim().toLowerCase().replace(/_/g, '-');
  if (!lower || lower === 'auto') return null;
  const exact = LANGUAGES.find((l) => l.code.toLowerCase() === lower);
  if (exact) return exact.code;
  const base = lower.split('-')[0];
  const byBase = LANGUAGES.find((l) => l.code.toLowerCase().split('-')[0] === base);
  return byBase ? byBase.code : null;
}

export function currentLocale(): string {
  return current;
}

export function currentLanguage(): Language {
  return LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[0];
}

/** Has the payer explicitly chosen a language (URL or stored)? Session hints then do not override. */
export function hasExplicitLanguage(): boolean {
  if (resolveLanguage(new URLSearchParams(location.search).get('lang'))) return true;
  return !!storedLanguage();
}

function storedLanguage(): string | null {
  try {
    return resolveLanguage(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function applyDocument(): void {
  const lang = currentLanguage();
  const root = document.documentElement;
  root.lang = lang.code;
  root.dir = lang.dir;
  document.title = t('app.title');
}

async function load(code: string): Promise<Dict> {
  if (dicts[code]) return dicts[code];
  const loader = loaders[`./${code}.json`];
  if (!loader) return en;
  const mod = await loader();
  dicts[code] = mod.default;
  return dicts[code];
}

/**
 * Switch language. `remember` persists the choice so it wins over session hints and the
 * browser language next time (pass false for hints applied on the payer's behalf).
 */
export async function setLocale(tag: string, remember = false): Promise<void> {
  const code = resolveLanguage(tag) ?? 'en';
  if (remember) {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* storage unavailable */
    }
  }
  await load(code);
  if (current === code && version > 0) return;
  current = code;
  applyDocument();
  notify();
}

/**
 * Initial language: `?lang=`, then the payer's stored choice, then (later, from the session)
 * the merchant's `locale` hint, then `navigator.languages`, then English.
 * The session hint is applied by the app once the session arrives (see `hasExplicitLanguage`).
 */
export function detectLocale(): string {
  const url = resolveLanguage(new URLSearchParams(location.search).get('lang'));
  if (url) return url;
  const stored = storedLanguage();
  if (stored) return stored;
  for (const tag of navigator.languages ?? [navigator.language]) {
    const r = resolveLanguage(tag);
    if (r) return r;
  }
  return 'en';
}

export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const raw = dicts[current]?.[key] ?? en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) => (k in vars ? String(vars[k]) : `{${k}}`));
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function localeVersion(): number {
  return version;
}
